import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { Settings2 } from "@/components/icons"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"

import { EditorSectionCard } from "./editor-fields"
import { useAdminRoutesQuery } from "../../hooks/admin-queries"

type AdvancedSectionProps = {
  editor: LibraryEditor
  /** Fetch routes only while the dialog is open. */
  enabled: boolean
}

export function AdvancedSection({ editor, enabled }: AdvancedSectionProps) {
  const { t } = useTranslation()
  const routesQuery = useAdminRoutesQuery(enabled)
  const routes = routesQuery.data?.filter((route) => route.enabled) ?? []
  const blocked = editor.values.blocked_modules

  const toggle = (routeId: string, isBlocked: boolean) => {
    const without = blocked.filter((id) => id !== routeId)
    editor.update({ blocked_modules: isBlocked ? [...without, routeId] : without })
  }

  return (
    <EditorSectionCard
      icon={<Settings2 className="size-4" />}
      id="library-advanced"
      open={editor.openSections.advanced}
      title={t("adminDashboard.libraries.editor.sections.advanced", "Advanced")}
      onOpenChange={(open) => editor.setSectionOpen("advanced", open)}
    >
      <div className="space-y-2">
        <Label>{t("adminDashboard.libraries.editor.blockFromRoutes", "Block from routes")}</Label>
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.libraries.editor.blockFromRoutesHelp",
            "Routes that must not have access to this library. Useful for separating content between explorers."
          )}
        </p>
        {routesQuery.isLoading && (
          <div className="flex items-center gap-2 py-2">
            <Spinner className="size-4" />
            <span className="text-muted-foreground text-sm">
              {t("adminDashboard.libraries.editor.loadingRoutes", "Loading routes...")}
            </span>
          </div>
        )}
        {!routesQuery.isLoading && routes.length === 0 && (
          <p className="text-muted-foreground py-2 text-sm">
            {t("adminDashboard.libraries.editor.noRoutes", "No routes available")}
          </p>
        )}
        {!routesQuery.isLoading && routes.length > 0 && (
          <div className="bg-muted/30 space-y-2 rounded-md border p-3">
            {routes.map((route) => (
              <div key={route.id} className="flex items-center gap-2">
                <Checkbox
                  checked={blocked.includes(route.id)}
                  id={`block-route-${route.id}`}
                  onCheckedChange={(checked) => toggle(route.id, checked === true)}
                />
                <label className="cursor-pointer text-sm" htmlFor={`block-route-${route.id}`}>
                  {route.name || route.id}
                  <span className="text-muted-foreground ml-2 text-xs">({route.path})</span>
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    </EditorSectionCard>
  )
}
